import type { Camera } from '../engine/Camera';
import type { Vec2 } from '../utils/math';

/**
 * Input layer.
 *
 * Step 1 scope: mouse-steering (target world point under cursor) + boost (Space)
 * + eject (W). Listeners are attached to the canvas (passive where possible)
 * and unwired on dispose() — critical to prevent leaks under React StrictMode
 * double-mount.
 */
export class InputManager {
  /** Latest cursor position in CSS pixels relative to canvas. */
  public mouseScreen: Vec2 = { x: 0, y: 0 };
  /** Latest cursor position projected into world space (refreshed each frame by engine). */
  public mouseWorld: Vec2 = { x: 0, y: 0 };

  public boost = false;
  public ejectQueued = false;

  /**
   * One-shot callback fired on the FIRST user interaction (keypress, click,
   * touch). Use it to unlock the Web Audio context — browsers block audio
   * until a real user gesture has fired.
   */
  public onFirstInteraction: (() => void) | null = null;
  /** Fired when the player presses M — toggles mute. */
  public onMuteToggle: (() => void) | null = null;
  /** Fired when the page loses focus — engine can pause. */
  public onPauseChange: ((paused: boolean) => void) | null = null;
  /** Fired when the player presses Escape — engine toggles the pause modal. */
  public onPauseToggle: (() => void) | null = null;

  /** Public mobile-touch entry points: HUD overlay buttons call these to
   *  drive boost + eject without a keyboard. */
  public setBoost(on: boolean): void { this.boost = on; }
  public queueEject(): void { this.ejectQueued = true; }

  /** ── Mobile virtual joystick ──────────────────────────────────
   *
   *  `mobileSteerAngle` is the ABSOLUTE world-space direction the
   *  joystick is pushed toward, in radians. Camera is axis-aligned,
   *  so screen-up = world-up; the joystick component computes
   *  atan2(dy, dx) from its origin and feeds it here directly.
   *
   *  When non-null, refresh() ignores mouseScreen and projects
   *  mouseWorld FAR in this direction from the snake's head. The
   *  snake's existing steer-toward-target physics then turns smoothly
   *  toward it. When the player releases the joystick, this resets
   *  to null and the snake continues in its last heading (which is
   *  what slither.io does — no abrupt stop, just "no input").
   */
  public mobileSteerAngle: number | null = null;
  public setMobileSteer(angle: number | null): void {
    this.mobileSteerAngle = angle;
  }
  private firstInteractionFired = false;

  private readonly target: HTMLElement;
  /**
   * Cached bounding rect of the canvas. Reading getBoundingClientRect()
   * inside the mousemove handler caused a layout-reflow on every event —
   * on a high-polling mouse this is hundreds of forced reflows per second
   * and produces perceptible input lag on large displays.
   *
   * We snapshot the rect at construction and refresh it on resize. Modern
   * canvases don't shift position outside of those events, so the cached
   * value stays valid.
   */
  private rectLeft = 0;
  private rectTop = 0;
  private resizeObserver: ResizeObserver | null = null;

  constructor(target: HTMLElement) {
    this.target = target;
    this.refreshRect();
    target.addEventListener('mousemove', this.onMouseMove, { passive: true });
    target.addEventListener('touchmove', this.onTouchMove, { passive: true });
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    window.addEventListener('focus', this.onFocus);
    window.addEventListener('resize', this.refreshRect);
    window.addEventListener('scroll', this.refreshRect, { passive: true });
    // Unlock-audio listeners are bound at the DOCUMENT level so they fire
    // for clicks on HUD buttons (name dialog, mute toggle, etc.) too — not
    // just on the canvas. Browser autoplay policy requires a real gesture
    // anywhere on the page.
    document.addEventListener('pointerdown', this.onPointerActivity, { passive: true });
    document.addEventListener('touchstart', this.onPointerActivity, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.refreshRect);
      this.resizeObserver.observe(target);
    }
  }

  private refreshRect = (): void => {
    const rect = this.target.getBoundingClientRect();
    this.rectLeft = rect.left;
    this.rectTop = rect.top;
  };

  dispose(): void {
    this.target.removeEventListener('mousemove', this.onMouseMove);
    this.target.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('resize', this.refreshRect);
    window.removeEventListener('scroll', this.refreshRect);
    document.removeEventListener('pointerdown', this.onPointerActivity);
    document.removeEventListener('touchstart', this.onPointerActivity);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private fireFirstInteraction(): void {
    if (this.firstInteractionFired) return;
    this.firstInteractionFired = true;
    this.onFirstInteraction?.();
  }

  private onPointerActivity = (): void => {
    this.fireFirstInteraction();
  };

  /** Engine calls this once per frame to refresh mouseWorld. Two
   *  input paths feed it:
   *    1. Mobile joystick: `mobileSteerAngle` is non-null → project
   *       mouseWorld FAR in that direction from the snake's head.
   *    2. Mouse / touch: camera unproject from `mouseScreen`.
   */
  refresh(camera: Camera, snakeHeadX?: number, snakeHeadY?: number): void {
    if (this.mobileSteerAngle !== null && snakeHeadX !== undefined && snakeHeadY !== undefined) {
      const FAR = 1200;
      this.mouseWorld.x = snakeHeadX + Math.cos(this.mobileSteerAngle) * FAR;
      this.mouseWorld.y = snakeHeadY + Math.sin(this.mobileSteerAngle) * FAR;
    } else {
      camera.screenToWorld(this.mouseScreen.x, this.mouseScreen.y, this.mouseWorld);
    }
  }

  /** Engine consumes this each tick; one-shot edge trigger. */
  consumeEject(): boolean {
    if (this.ejectQueued) {
      this.ejectQueued = false;
      return true;
    }
    return false;
  }

  private onMouseMove = (e: MouseEvent): void => {
    // Uses the cached rect — no per-event layout reflow.
    this.mouseScreen.x = e.clientX - this.rectLeft;
    this.mouseScreen.y = e.clientY - this.rectTop;
  };

  private onTouchMove = (e: TouchEvent): void => {
    const t = e.touches[0];
    if (!t) return;
    this.mouseScreen.x = t.clientX - this.rectLeft;
    this.mouseScreen.y = t.clientY - this.rectTop;
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    this.fireFirstInteraction();
    if (e.code === 'Space') {
      this.boost = true;
      e.preventDefault();
    } else if (e.code === 'KeyW') {
      this.ejectQueued = true;
    } else if (e.code === 'KeyM') {
      this.onMuteToggle?.();
    } else if (e.code === 'Escape') {
      this.onPauseToggle?.();
      e.preventDefault();
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (e.code === 'Space') this.boost = false;
  };

  // Window blur/focus used to auto-pause the game — removed per user
  // feedback: the pause modal kept popping up whenever focus drifted to
  // another element (address bar, devtools, another tab, alt-tab). The
  // input safety cleanup stays (so boost doesn't stick when the user
  // tabs away mid-hold), but pause is now strictly a manual action via
  // the Escape key + the in-HUD pause button.
  private onBlur = (): void => {
    this.boost = false;
    this.ejectQueued = false;
  };

  private onFocus = (): void => {
    // intentional no-op — see onBlur. Kept as a registered listener so
    // the dispose() pairing stays symmetric and no future "auto-resume"
    // accidentally re-introduces auto-pause via the focus side.
  };
}
